import { useCallback } from 'react';
import { useRouter } from 'expo-router';
import { Linking } from 'react-native';

import { logger } from '@garnish/logger';
import { useNoticeBannersStackActions } from '@garnish/overlays';

import {
  checkIfInternalUrl,
  formatUniversalURL,
  getAppAssociatedDomains,
} from '../../utils/linking';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A universal (aka cross-platform) URLs handler.
 *
 * Returns a helper that can be used to open both internal and external URLs
 * such as 'http://www.example.com', '/in-app-route', .etc.
 *
 * Dependencies:
 *
 * - `NoticeBannerStackProvider` (Garnish)
 * - expo-router root layout
 *
 * App associated domains
 *
 * This hook additionally supports internal URLs that begin with one of the
 * associated app domains, which may be provided as an Expo app env variable.
 * Hook will check for the mentioned env variable, which can be specified
 * in the `expo.extra` object of the Expo `app.config` file.
 *
 * @see {@link https://docs.expo.dev/guides/environment-variables/#reading-environment-variables}
 */
export const useOpenURL = () => {
  const router = useRouter();
  const { push: addBanner } = useNoticeBannersStackActions();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const showErrorBanner = useCallback(
    (errorMessage: string) => {
      addBanner({ palette: 'caution', text: errorMessage });
    },
    [addBanner],
  );

  /**
   * Tries to open external URL using system browser.
   */
  const openExternalUrl = useCallback<UrlHandler>(
    async (url, errorMessage) => {
      const canOpenUrl = await Linking.canOpenURL(url);

      if (!canOpenUrl) {
        if (errorMessage) showErrorBanner(errorMessage);

        return;
      }

      await Linking.openURL(url);
    },
    [showErrorBanner],
  );

  /**
   * Tries to open provided internal URL via expo-router.
   */
  const openInternalUrl = useCallback<UrlHandler>(
    (url, errorMessage) => {
      try {
        router.navigate(url as never);
      } catch (error: unknown) {
        if (errorMessage) showErrorBanner(errorMessage);

        messageLogger.error(error);
      }
    },
    [router, showErrorBanner],
  );

  // ─────────────────────────────────────────────────────────────────────

  return useCallback<UrlHandler>(
    (url, errorMessage) => {
      const formattedUrl = formatUniversalURL(url, APP_ASSOCIATED_DOMAINS);
      const isInternalURL = checkIfInternalUrl(formattedUrl);

      if (isInternalURL) {
        openInternalUrl(formattedUrl, errorMessage);

        return;
      }

      openExternalUrl(url, errorMessage);
    },
    [openExternalUrl, openInternalUrl],
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const APP_ASSOCIATED_DOMAINS = getAppAssociatedDomains();

// ─── Logger ──────────────────────────────────────────────────────────────────

const messageLogger = logger.extend('useOpenURL');

// ─── Types ───────────────────────────────────────────────────────────────────

type UrlHandler = (url: string, errorMessage?: string) => void;
